import { Modifier } from '@dnd-kit/core'
import { IDndItem } from './types'

export const restrictToSpecificContainer = (containerId: string): Modifier => {
  return args => {
    const { transform, draggingNodeRect } = args

    if (!draggingNodeRect) {
      return transform
    }

    const container = document.getElementById(containerId)
    if (!container) {
      return transform
    }

    const containerRect = container.getBoundingClientRect()

    return {
      ...transform,
      x: Math.min(
        Math.max(transform.x, containerRect.left - draggingNodeRect.left),
        containerRect.right - draggingNodeRect.right
      ),
      y: Math.min(
        Math.max(transform.y, containerRect.top - draggingNodeRect.top),
        containerRect.bottom - draggingNodeRect.bottom
      )
    }
  }
}

export const groupByLineIndex = (items: IDndItem[]): IDndItem[][] => {
  const result: IDndItem[][] = []
  items.forEach(item => {
    if (!result[item.lineIndex]) {
      result[item.lineIndex] = []
    }
    result[item.lineIndex].push(item)
  })
  return result
}

export const calculateNewPositions = ({
  draggedItem,
  items,
  dropLineIndex,
  containerWidth,
  deltaX
}: {
  draggedItem: IDndItem
  items: IDndItem[]
  dropLineIndex: number
  containerWidth: number
  deltaX: number
}) => {
  const draggedItemLeft = Math.min(
    draggedItem.left + deltaX,
    containerWidth - draggedItem.width
  )

  const itemsInLine = items.filter(item => item.lineIndex === dropLineIndex)
  const nonDraggedItems = itemsInLine.filter(item => item.id !== draggedItem.id)

  const reorderedItems = [
    ...nonDraggedItems,
    { ...draggedItem, left: draggedItemLeft }
  ].sort((a, b) => a.left - b.left)

  const draggedIndex = reorderedItems.findIndex(
    item => item.id === draggedItem.id
  )
  const prevItemRightEdge =
    draggedIndex > 0
      ? reorderedItems[draggedIndex - 1].left +
        reorderedItems[draggedIndex - 1].width
      : 0
  const nextItemLeftEdge =
    draggedIndex < reorderedItems.length - 1
      ? reorderedItems[draggedIndex + 1].left
      : containerWidth

  const availableSpace = nextItemLeftEdge - prevItemRightEdge

  let finalLeftPosition = draggedItemLeft
  let leftShiftAmount = 0

  if (availableSpace < draggedItem.width) {
    finalLeftPosition = prevItemRightEdge
    leftShiftAmount = draggedItem.width - availableSpace
  } else {
    if (draggedItemLeft + draggedItem.width > nextItemLeftEdge) {
      finalLeftPosition = nextItemLeftEdge - draggedItem.width
    } else if (draggedItemLeft < prevItemRightEdge) {
      finalLeftPosition = prevItemRightEdge
    }
  }

  return items.map(item => {
    if (item.id === draggedItem.id) {
      return {
        ...item,
        lineIndex: dropLineIndex,
        left: finalLeftPosition
      }
    }

    const isItemInLine = item.lineIndex === dropLineIndex
    if (isItemInLine && item.left > draggedItemLeft) {
      return { ...item, left: item.left + leftShiftAmount }
    }

    return item
  })
}

export const updateDropIndicators = ({
  containerRef,
  left,
  width,
  lineIndex
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  left: number
  width: number
  lineIndex: number
}) => {
  if (!containerRef.current) return
  const dropIndicators =
    containerRef.current.querySelectorAll('.drop-indicator')
  dropIndicators.forEach(indicator => {
    const indicatorEl = indicator as HTMLElement // fix typescript type error
    const isDropLine = indicatorEl.dataset.lineIndex === lineIndex.toString()
    if (isDropLine) {
      indicatorEl.style.display = 'block'
      indicatorEl.style.left = `${left}px`
      indicatorEl.style.width = `${width}px`
    } else {
      indicatorEl.style.display = 'none'
    }
  })
}

export const hideDropIndicators = (
  containerRef: React.RefObject<HTMLDivElement | null>
) => {
  if (!containerRef.current) return
  const dropIndicators =
    containerRef.current.querySelectorAll('.drop-indicator')
  dropIndicators.forEach(indicator => {
    const indicatorEl = indicator as HTMLElement // fix typescript type error
    indicatorEl.style.display = 'none'
  })
}

export const getIsOverlapDuringDrag = (
  draggedItem: IDndItem,
  items: IDndItem[],
  lineIndex: number,
  deltaX: number
): boolean => {
  // Calculate current dragged position
  const currentLeft = draggedItem.left + deltaX
  const currentRight = currentLeft + draggedItem.width

  // Check for overlaps with items in the same line
  const itemsInLine = items.filter(
    item => item.lineIndex === lineIndex && item.id !== draggedItem.id
  )

  // Return true as soon as any overlap is found
  return itemsInLine.some(item => {
    const itemLeft = item.left
    const itemRight = item.left + item.width

    // Check if there's any overlap
    return currentLeft < itemRight && currentRight > itemLeft
  })
}
